import { JourneyDayMealItem, JourneyDayTripItem } from "../../models/axiosModel"

export const MEAL_TYPE_BREAKFAST = 1
export const MEAL_TYPE_LUNCH = 2
export const MEAL_TYPE_DINNER = 3

export const MEAL_TYPE_IDS = [MEAL_TYPE_BREAKFAST, MEAL_TYPE_LUNCH, MEAL_TYPE_DINNER] as const

/**
 * Zero-price policy when `flag_*_included` is true.
 *
 * - `included_always` — any trip with included flag → meal unit price 0 (current default; colleague rule)
 * - `cruise_only` — only cruise-like trips with included flag → 0; other tours keep city default
 *
 * Flip this one constant to switch behavior when PM confirms the opposite rule.
 * See planning doc §4.10.
 */
export type MealIncludedZeroPriceMode = "included_always" | "cruise_only"

export const MEAL_INCLUDED_ZERO_PRICE_MODE: MealIncludedZeroPriceMode = "included_always"

export type MealIncludedFlags = {
  flag_breakfast_included?: boolean
  flag_lunch_included?: boolean
  flag_dinner_included?: boolean
}

export type CruiseLikeTripFields = MealIncludedFlags & {
  day_trip_type_id?: number | null
  day_trip_name?: string | null
}

const MEAL_FLAG_BY_TYPE: Record<number, keyof MealIncludedFlags> = {
  [MEAL_TYPE_BREAKFAST]: "flag_breakfast_included",
  [MEAL_TYPE_LUNCH]: "flag_lunch_included",
  [MEAL_TYPE_DINNER]: "flag_dinner_included",
}

const DEFAULT_MEAL_NAMES: Record<number, string> = {
  [MEAL_TYPE_BREAKFAST]: "Breakfast",
  [MEAL_TYPE_LUNCH]: "Lunch",
  [MEAL_TYPE_DINNER]: "Dinner",
}

/** Cruise = type id 3, or day_trip_name contains "cruise" (case-insensitive). */
export const isCruiseLikeTrip = (trip: CruiseLikeTripFields | undefined | null): boolean => {
  if (!trip) return false
  if (Number(trip.day_trip_type_id) === 3) return true
  return String(trip.day_trip_name ?? "").toLowerCase().includes("cruise")
}

export const includedFlagForMealType = (mealTypeId: number): keyof MealIncludedFlags | null =>
  MEAL_FLAG_BY_TYPE[Number(mealTypeId)] ?? null

export const tripHasMealIncluded = (
  trip: MealIncludedFlags | undefined | null,
  mealTypeId: number,
): boolean => {
  const key = includedFlagForMealType(mealTypeId)
  if (!key || !trip) return false
  return Boolean(trip[key])
}

export const tripHasDinnerIncluded = (trip: MealIncludedFlags | undefined | null): boolean =>
  tripHasMealIncluded(trip, MEAL_TYPE_DINNER)

/** Whether an included meal should be unit-priced at 0 under the active (or overridden) mode. */
export const shouldZeroIncludedMealPrice = (args: {
  isCruiseLike: boolean
  flagIncluded: boolean
  mode?: MealIncludedZeroPriceMode
}): boolean => {
  if (!args.flagIncluded) return false
  const mode = args.mode ?? MEAL_INCLUDED_ZERO_PRICE_MODE
  if (mode === "included_always") return true
  return Boolean(args.isCruiseLike)
}

/**
 * Resolve unit price for an included (or candidate) meal.
 * - flag not included → city default (caller usually won't call this for that case)
 * - flag included + mode says zero → 0
 * - else → city default
 */
export const resolveIncludedMealUnitPrice = (args: {
  isCruiseLike: boolean
  flagIncluded: boolean
  cityDefaultPrice: number
  mode?: MealIncludedZeroPriceMode
}): number => {
  if (
    shouldZeroIncludedMealPrice({
      isCruiseLike: args.isCruiseLike,
      flagIncluded: args.flagIncluded,
      mode: args.mode,
    })
  ) {
    return 0
  }
  return Number(args.cityDefaultPrice) || 0
}

/** True when any trip on the day has the matching included flag. */
export const dayHasMealIncluded = (
  trips: CruiseLikeTripFields[] | undefined | null,
  mealTypeId: number,
): boolean => (trips || []).some((t) => tripHasMealIncluded(t, mealTypeId))

/** True when any cruise-like trip on the day has the matching included flag. */
export const dayHasCruiseIncludedMeal = (
  trips: CruiseLikeTripFields[] | undefined | null,
  mealTypeId: number,
): boolean =>
  (trips || []).some((t) => isCruiseLikeTrip(t) && tripHasMealIncluded(t, mealTypeId))

/** True when day should force unit price 0 for this meal type under the active mode. */
export const dayHasZeroPricedIncludedMeal = (
  trips: CruiseLikeTripFields[] | undefined | null,
  mealTypeId: number,
  mode: MealIncludedZeroPriceMode = MEAL_INCLUDED_ZERO_PRICE_MODE,
): boolean => {
  if (mode === "included_always") return dayHasMealIncluded(trips, mealTypeId)
  return dayHasCruiseIncludedMeal(trips, mealTypeId)
}

export const snapshotTripMealIncludedFields = (
  source: CruiseLikeTripFields | undefined | null,
  fallback?: CruiseLikeTripFields | null,
): Required<MealIncludedFlags> & { day_trip_type_id: number; day_trip_name: string } => ({
  day_trip_type_id: Number(source?.day_trip_type_id ?? fallback?.day_trip_type_id) || 0,
  day_trip_name: String(source?.day_trip_name ?? fallback?.day_trip_name ?? ""),
  flag_breakfast_included: Boolean(
    source?.flag_breakfast_included ?? fallback?.flag_breakfast_included,
  ),
  flag_lunch_included: Boolean(source?.flag_lunch_included ?? fallback?.flag_lunch_included),
  flag_dinner_included: Boolean(source?.flag_dinner_included ?? fallback?.flag_dinner_included),
})

/**
 * Hydrate trusts BE meal prices (including 0). Never recompute on load.
 * Exposed for unit tests and explicit call sites that must not overwrite.
 */
export const trustHydratedMealPrices = <T extends { price?: number }>(
  meals: T[] | undefined | null,
): T[] => (Array.isArray(meals) ? meals.map((m) => ({ ...m })) : [])

export type ApplyTripIncludedMealsResult = {
  meals: JourneyDayMealItem[]
  dinnerWasAdded: boolean
}

/**
 * Option A: apply only the given trip's included flags.
 * - Auto-select missing meals when flag is true
 * - Zero unit price when mode says so (also drops existing city price to 0)
 */
export const applyTripIncludedMeals = (args: {
  meals: JourneyDayMealItem[] | undefined | null
  trip: CruiseLikeTripFields
  cityDefaultPriceFor: (mealTypeId: number) => number
  mealNames?: Record<number, string>
  mode?: MealIncludedZeroPriceMode
}): ApplyTripIncludedMealsResult => {
  const names = args.mealNames ?? DEFAULT_MEAL_NAMES
  const mode = args.mode ?? MEAL_INCLUDED_ZERO_PRICE_MODE
  const cruiseLike = isCruiseLikeTrip(args.trip)
  let nextMeals = [...(args.meals || [])]
  const hadDinner = nextMeals.some((m) => Number(m.quotation_meal_type_id) === MEAL_TYPE_DINNER)

  for (const mealTypeId of MEAL_TYPE_IDS) {
    const flagIncluded = tripHasMealIncluded(args.trip, mealTypeId)
    if (!flagIncluded) continue

    const cityDefault = args.cityDefaultPriceFor(mealTypeId)
    const unitPrice = resolveIncludedMealUnitPrice({
      isCruiseLike: cruiseLike,
      flagIncluded: true,
      cityDefaultPrice: cityDefault,
      mode,
    })
    const existingIdx = nextMeals.findIndex((m) => Number(m.quotation_meal_type_id) === mealTypeId)
    const shouldZero = shouldZeroIncludedMealPrice({
      isCruiseLike: cruiseLike,
      flagIncluded: true,
      mode,
    })

    if (existingIdx < 0) {
      nextMeals.push({
        quotation_meal_type_id: mealTypeId,
        quotation_meal_type_name: names[mealTypeId] ?? `Meal ${mealTypeId}`,
        price: unitPrice,
      })
    } else if (shouldZero) {
      // Drop existing city price to 0 when included meal is zero-priced under active mode.
      nextMeals = nextMeals.map((m, i) => (i === existingIdx ? { ...m, price: 0 } : m))
    }
  }

  const dinnerWasAdded =
    !hadDinner && nextMeals.some((m) => Number(m.quotation_meal_type_id) === MEAL_TYPE_DINNER)

  return { meals: nextMeals, dinnerWasAdded }
}

/**
 * After any trip remove:
 * - If removed trip included meal M and no remaining trip includes M → deselect M
 * - If kept:
 *   - `included_always` → keep at 0 (Option X)
 *   - `cruise_only` → restore city default if no cruise-included M remains
 */
export const syncMealsAfterTripRemoved = (args: {
  meals: JourneyDayMealItem[] | undefined | null
  removedTrip: CruiseLikeTripFields
  remainingTrips: CruiseLikeTripFields[]
  cityDefaultPriceFor: (mealTypeId: number) => number
  mode?: MealIncludedZeroPriceMode
}): JourneyDayMealItem[] => {
  const mode = args.mode ?? MEAL_INCLUDED_ZERO_PRICE_MODE
  let next = [...(args.meals || [])]

  for (const mealTypeId of MEAL_TYPE_IDS) {
    if (!tripHasMealIncluded(args.removedTrip, mealTypeId)) continue

    const stillIncluded = args.remainingTrips.some((t) => tripHasMealIncluded(t, mealTypeId))
    if (!stillIncluded) {
      next = next.filter((m) => Number(m.quotation_meal_type_id) !== mealTypeId)
      continue
    }

    if (mode === "included_always") {
      next = next.map((m) =>
        Number(m.quotation_meal_type_id) === mealTypeId ? { ...m, price: 0 } : m,
      )
      continue
    }

    // cruise_only: restore city default if no cruise-included remains.
    if (!dayHasCruiseIncludedMeal(args.remainingTrips, mealTypeId)) {
      const cityDefault = args.cityDefaultPriceFor(mealTypeId)
      next = next.map((m) =>
        Number(m.quotation_meal_type_id) === mealTypeId ? { ...m, price: cityDefault } : m,
      )
    }
  }

  return next
}

/** Resolve unit price when the user manually turns a meal on (C1 under included_always). */
export const resolveManualMealTogglePrice = (args: {
  trips: CruiseLikeTripFields[] | undefined | null
  mealTypeId: number
  cityDefaultPrice: number
  mode?: MealIncludedZeroPriceMode
}): number => {
  const mode = args.mode ?? MEAL_INCLUDED_ZERO_PRICE_MODE
  if (dayHasZeroPricedIncludedMeal(args.trips, args.mealTypeId, mode)) return 0
  return Number(args.cityDefaultPrice) || 0
}

/** Read included + cruise fields from a selected day trip (for DT / remove sync). */
export const asCruiseLikeTripFields = (
  trip: JourneyDayTripItem | CruiseLikeTripFields | undefined | null,
): CruiseLikeTripFields => ({
  day_trip_type_id: (trip as any)?.day_trip_type_id,
  day_trip_name: (trip as any)?.day_trip_name,
  flag_breakfast_included: Boolean((trip as any)?.flag_breakfast_included),
  flag_lunch_included: Boolean((trip as any)?.flag_lunch_included),
  flag_dinner_included: Boolean((trip as any)?.flag_dinner_included),
})
